import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import { WithApiClientMixin } from '@theme/mixin-with-api-client'
import { Task, html, unsafeHTML } from '@theme/vendor-lit'

const GET_PRODUCT_VARIANTS_FROM_NODE = `#graphql
  query getProductVariantsFromNode($id: ID!) {
    node(id: $id) {
      id
      ... on ProductVariant {
        product {
          handle
        }
      }
    }
  }
`

const CACHE = {}
const VARIANT_HANDLE_CACHE = {}

export class DynamicProductCard extends WithApiClientMixin(
  BaseElementWithoutShadowDOM
) {
  constructor() {
    super(...arguments)

    this.template = 'product-card'
    this.checkIfResponseHasLayout = (response) => {
      return response.match(/html|body/)
    }

    this._loadVariantByIdTask = new Task(this, {
      task: async ([variantId]) => {
        if (!variantId) {
          throw new Error('No variantId provided')
        }

        if (VARIANT_HANDLE_CACHE[variantId]) {
          return VARIANT_HANDLE_CACHE[variantId]
        }

        const request = await this.$api.client.storefront({
          body: {
            query: GET_PRODUCT_VARIANTS_FROM_NODE,
            variables: {
              id: `gid://shopify/ProductVariant/${variantId}`,
            },
          },
        })

        if (!request.data?.node?.product?.handle) {
          throw new Error('No product handle found')
        }

        VARIANT_HANDLE_CACHE[variantId] = request.data?.node?.product?.handle
        return VARIANT_HANDLE_CACHE[variantId]
      },
      args: () => [this.variantId],
    })

    this.loadProductHandleView = new Task(this, {
      task: async ([handle]) => {
        if (!handle) {
          throw new Error('No handle provided')
        }

        if (CACHE[this.cacheKey]) {
          return CACHE[this.cacheKey]
        }

        const request = await this.$api.client.shopify(
          `products/${handle}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'text/html',
            },
          },
          {
            params: {
              view: this.template,
              layout: 'none',
              ...(this.variantId && {
                variant: this.variantId,
              }),
            },
          }
        )
        if (this.checkIfResponseHasLayout(request)) {
          throw new Error('Invalid response')
        }

        if (request) {
          CACHE[this.cacheKey] = request
          return request
        }

        throw new Error('No product found')
      },
      args: () => [
        this.handle || this._loadVariantByIdTask.value,
        this.variantId,
      ],
    })
  }
  static get properties() {
    return {
      variantId: { type: String, attribute: 'variant-id' },
      handle: { type: String },
      template: { type: String },
    }
  }

  createRenderRoot() {
    return this
  }

  get cacheKey() {
    return `${this.handle}-${this.variantId}-${this.template}`
  }

  render() {
    return html`
      ${this.loadProductHandleView.render({
        complete: (data) => {
          if (!data) return
          return html`${unsafeHTML(data)}`
        },
      })}
    `
  }
}

customElements.define('dynamic-product-card', DynamicProductCard)
